import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { TooltipProvider, Toaster } from '@cou/admin-ui';
import './styles.css';
import { AdminAuthProvider } from './session/admin-auth-context';
import { routes } from './routes';

// Path-based routing (createBrowserRouter, not the frontend's createHashRouter): the admin is a
// standalone operator console, not the iframe-embedded player app, so it uses clean path URLs
// (/catalogue, /settlement) instead of hash URLs. Deep links and refreshes resolve in prod because
// Azure Static Web Apps rewrites unknown paths to /index.html (public/staticwebapp.config.json),
// and in dev because Vite's SPA fallback does the same.
const router = createBrowserRouter(routes);

const container = document.getElementById('root');
if (!container) {
  throw new Error('#root element not found');
}

const root = createRoot(container);

// Dev-only admin component gallery (CAE-279), mounted at /gallery OUTSIDE the router tree —
// admin-ui components are pure presentational. `import.meta.env.DEV` is statically false in a
// production build, so this whole branch (and the dynamic import) is dead-code-eliminated.
// Path-based: editing the address bar to /gallery is a cross-path navigation, so the browser does a
// full load that re-runs this check — unlike a hash change, which the old one-time read never saw
// (that was the "only loads after refresh" bug).
if (import.meta.env.DEV && window.location.pathname.startsWith('/gallery')) {
  void import('./gallery/gallery').then(({ Gallery }) => {
    root.render(
      <StrictMode>
        <Gallery />
      </StrictMode>,
    );
  });
} else {
  // The composition root wires the app's cross-cutting providers once: the admin auth context (token
  // + decoded claims that drive the guard and nav), the design system's shared TooltipProvider, and
  // the Sonner Toaster (imperative toast target for later ops feedback).
  root.render(
    <StrictMode>
      <AdminAuthProvider>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster />
        </TooltipProvider>
      </AdminAuthProvider>
    </StrictMode>,
  );
}
